/**
 * Detects the harmless browser-level ResizeObserver loop error.
 *
 * Modern browsers emit this when a ResizeObserver callback cannot be
 * delivered within a single frame. It surfaces through `window.onerror`
 * with no actionable in-app stack trace, gets captured by Sentry, and
 * pages `#one-vitals` without indicating any real user-visible breakage.
 *
 * Known canonical messages:
 *   - Chromium / Firefox / Edge: `ResizeObserver loop completed with undelivered notifications.`
 *   - Older Chromium / WebKit:   `ResizeObserver loop limit exceeded`
 *
 * Matched against the *whole* message (case-insensitive, trailing period
 * tolerated), not as a substring, so a hand-written
 * `throw new Error('ResizeObserver setup failed for X')` or a log line
 * that merely mentions ResizeObserver is not mis-classified — only the
 * browser's own canonical messages match.
 *
 * For wrappers like `InternalError` that preserve the inner browser
 * error on an `errorMessage` field, that field is also matched.
 */
const KNOWN_RESIZE_OBSERVER_LOOP_MESSAGES: ReadonlySet<string> = new Set([
  'resizeobserver loop completed with undelivered notifications',
  'resizeobserver loop limit exceeded',
]);

// Strips the optional `TypeError:` / `Error:` prefix that useFetch leaves on
// stringified `error.value`s, so we can compare just the canonical message.
const STRINGIFIED_PREFIX_PATTERN = /^(?:typeerror|error):\s+/;

function matchesKnownResizeObserverLoopMessage(text: string): boolean {
  const normalized = text
    .toLowerCase()
    .trim()
    .replace(STRINGIFIED_PREFIX_PATTERN, '')
    .replace(/\.$/, '');
  return KNOWN_RESIZE_OBSERVER_LOOP_MESSAGES.has(normalized);
}

export function isResizeObserverLoopError(error: unknown): boolean {
  if (error === null || error === undefined || error === '') {
    return false;
  }

  if (typeof error === 'string') {
    return matchesKnownResizeObserverLoopMessage(error);
  }

  if (error instanceof Error) {
    if (matchesKnownResizeObserverLoopMessage(error.message)) {
      return true;
    }

    // InternalError-style wrappers preserve the inner browser error's
    // `.message` on an `errorMessage` field. Honor it so a wrapped
    // ResizeObserver loop error is still recognized.
    const inner = (error as { errorMessage?: unknown }).errorMessage;
    if (typeof inner === 'string' && matchesKnownResizeObserverLoopMessage(inner)) {
      return true;
    }

    return false;
  }

  return false;
}
